import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import furoApi from '../api/FuroApi';
import {COST_BEARER_RENTER, ErrorResponse, MaintenanceHistory} from '../api/dtos/dtos';
import locales from '../locales';
import * as paths from '../paths';
import {ErrorAlert} from '../common/ErrorAlert';
import MaintenanceHistoryForm from './MaintenanceHistoryForm';
import {showConfirm, showSaveSuccessNotification} from '../utils/dialogs';
import {Button} from 'antd';
import ButtonBar from '../common/ButtonBar';
import {InvoiceEntry} from "../api/dtos/summaries";
import {AmountType} from "../common/AmountType";
import {InvoiceSource, InvoiceState, InvoiceType} from "../invoicing/invoice";
import accountProvider from '../account/AccountProvider';
import {showPrintPopup} from '../print/PrintPopup';
import PrintMaintenanceSheetPage from '../print/PrintMaintenanceSheetPage';
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from '../app/alert/GlobalAlertContextProvider';

interface State {
  editable: boolean;
  maintenanceTypes: string[];
  historyItem?: MaintenanceHistory;
  error?: ErrorResponse;
  saveError?: ErrorResponse;
}

class MaintenanceHistoryDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  public state: State = {
    editable: false,
    maintenanceTypes: [],
  };

  public componentDidMount() {
    this.loadMaintenanceTypes();
    this.loadMaintenanceHistoryItem();
  }

  public componentDidUpdate(prevProps: Readonly<RouteComponentProps<paths.IdPageUrlParams>>) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.loadMaintenanceHistoryItem();
    }
  }

  public render(): React.ReactNode {
    const {historyItem, maintenanceTypes, error, saveError, editable} = this.state;
    const isNotFinalized = historyItem && !historyItem.finalized;
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.maintenance, this)}
        </GlobalAlertContextConsumer>

        {historyItem && (
          <>
            <ButtonBar style={{marginBottom: 16}}>
              {accountProvider.isAdmin() && historyItem.costBearer === COST_BEARER_RENTER && historyItem.client && (!historyItem.invoice || historyItem.invoice.type === InvoiceType.STORNO) && isNotFinalized &&
                  <Button type="primary" icon="file-text"
                          onClick={this.onCreateInvoice}
                  >
                    {locales.invoice_create}
                  </Button>
              }
              {historyItem.costBearer === COST_BEARER_RENTER && isNotFinalized && (!historyItem.invoice || historyItem?.invoice.state === InvoiceState.DELETED) &&
                  <Button
                      onClick={this.onFinalizeClick}
                  >
                    {locales.finalize}
                  </Button>
              }
              {historyItem.costBearer === COST_BEARER_RENTER && !isNotFinalized && (!historyItem.invoice || historyItem?.invoice.state === InvoiceState.DELETED) &&
                  <Button
                      onClick={this.onFinalizeRevertClick}
                  >
                    {locales.finalized_revert}
                  </Button>
              }
              <Button icon="printer" disabled={!historyItem.item} onClick={this.onPrintClick}>
                {locales.print}
              </Button>
              <Button icon={historyItem.handled ? 'file-exclamation' : 'file-done'} onClick={this.onHandleClick}>
                {historyItem.handled ? locales.reopen : locales.handle}
              </Button>
              {isNotFinalized &&
                  <Button
                      type="primary"
                      icon="edit"
                      onClick={this.onEditClick}
                      disabled={editable}
                  >
                    {locales.edit}
                  </Button>
              }
            </ButtonBar>
            <MaintenanceHistoryForm
              editable={editable}
              types={maintenanceTypes}
              historyItem={historyItem}
              onFormSubmit={this.onSave}
              onFormCancel={this.onCancel}
              onAttachmentChange={this.loadMaintenanceHistoryItem}
              onHistoryItemReload={this.loadMaintenanceHistoryItem}
            />
          </>
        )}

        <ErrorAlert style={{marginTop: 16}} error={saveError || error}/>
      </>
    );
  }

  private getIdParam = (): number => Number(this.props.match.params.id);

  private loadMaintenanceTypes = () => {
    const promise = furoApi.maintenance.getMaintenanceTypes()
      .then(response => this.setState({maintenanceTypes: response.types}))
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadMaintenanceHistoryItem = () => {
    const promise = furoApi.maintenance.getMaintenanceHistoryItem(this.getIdParam())
      .then(this.onLoadSuccess)
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadSuccess = (historyItem: MaintenanceHistory) => {
    this.setState({historyItem, error: undefined});
    setAppHeaderSubtitle(this.props, historyItem.name || locales.maintenance_worksheet);
  };

  private onSave = (historyItem: MaintenanceHistory) => {
    if (historyItem.id == null) {
      this.setState({saveError: {message: locales.save_error}});
      return;
    }

    const promise = furoApi.maintenance.saveMaintenanceHistoryItem(historyItem, historyItem.id)
      .then(this.onSaveSuccess);

    promise.catch(this.onSaveError);
    withLoadingOverlay(promise, this.loadingHandler);
  };


  private onSaveSuccess = () => {
    showSaveSuccessNotification();
    this.setState({saveError: undefined, editable: false});
    this.loadMaintenanceHistoryItem();
  };

  private onSaveError = (saveError: ErrorResponse) => {
    this.setState({saveError});
  };

  private onError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onCancel = () => {
    this.setState({editable: false}, this.loadMaintenanceHistoryItem);
  };

  private onEditClick = () => {
    this.setState({editable: true});
  };

  private onPrintClick = () => {
    const {historyItem} = this.state;
    if (!historyItem || !historyItem.item) {
      return;
    }

    showPrintPopup(
      locales.maintenance_worksheet,
      <PrintMaintenanceSheetPage historyItem={historyItem} item={historyItem.item}/>
    );
  };

  private onHandleClick = () => {
    const {historyItem} = this.state;
    if (!historyItem) {
      return;
    }

    const handleConfirm = historyItem.handled ? locales.handle_item_maintenance_reopen : locales.handle_item_maintenance_close;
    showConfirm(handleConfirm, () => this.handleHistoryItem(historyItem));
  };

  private handleHistoryItem = (historyItem: MaintenanceHistory) => {
    if (historyItem.id == null) {
      return;
    }

    const promise = furoApi.maintenance.handleMaintenanceHistoryItem(historyItem.id, !historyItem.handled)
      .then(() => {
        showSaveSuccessNotification();
        this.alertController?.refreshAlertCount();
        this.loadMaintenanceHistoryItem();
      })
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onCreateInvoice = () => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    const {historyItem} = this.state;
    if (!historyItem) {
      return;
    }
    const entries: InvoiceEntry[] = historyItem.entries.map(entry => {
      const price = Number((entry.cost * (1 - (entry.discount ?? 0) / 100)).toFixed(1));

      return {
        name: entry.name,
        amount: entry.amount,
        price,
        amountType: AmountType.PCS,
        comment: entry.discount ? `${locales.discount}: ${entry.discount}%` : null,
        total: price * entry.amount,
      };
    });

    if (historyItem.repairCost !== undefined && (historyItem.repairTime ?? 0) > 0) {
      entries.push({
        name: locales.repair,
        amount: historyItem.repairTime,
        price: historyItem.repairCost!,
        amountType: AmountType.HOURLY,
        comment: null,
        total: historyItem.repairCost! * historyItem.repairTime!,
      });
    }

    if (historyItem.travelCost !== undefined && (historyItem.travelDistance ?? 0) > 0) {
      entries.push({
        name: locales.travel,
        amount: historyItem.travelDistance,
        price: historyItem.travelCost!,
        amountType: 'km',
        comment: null,
        total: historyItem.travelCost! * historyItem.travelDistance!,
      });
    }

    this.props.history.push(paths.createInvoicePath, {
      client: historyItem.client,
      entries,
      source: InvoiceSource.MAINTENANCE,
      sourceId: historyItem.id,
    });
  };

  private onFinalizeClick = () => {
    showConfirm(locales.finalize_maintenance, this.finalizeMaintenance);
  }

  private finalizeMaintenance = () => {
    const {historyItem} = this.state;
    if (!historyItem || historyItem.id == null) {
      return;
    }

    const promise = furoApi.maintenance.finalizeMaintenanceHistory(historyItem.id)
      .then(() => {
        this.setState({editable: false});
        this.loadMaintenanceHistoryItem();
      }).catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onFinalizeRevertClick = () => {
    showConfirm(locales.revert_finalize_confirm, this.revertFinalize);
  }

  private revertFinalize = () => {
    const {historyItem} = this.state;
    if (!historyItem || historyItem.id == null) {
      return;
    }

    const promise = furoApi.maintenance.revertFinalizedMaintenanceHistory(historyItem.id)
      .then(() => {
        this.setState({editable: false});
        this.loadMaintenanceHistoryItem();
      }).catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  }
}

export default withRouter(MaintenanceHistoryDetailsPage);



// WEBPACK FOOTER //
// ./src/maintenance/MaintenanceHistoryDetailsPage.tsx